import { forwardRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';

import { SCREEN_SIZES } from '@/shared/config/ui';

import MilepostProgress from '@/features/gameSession/common/components/MilepostProgress';
import Button from '@/shared/components/button/Button';

import '../styles/game-end-screen-milepost.scss';

import type { ForwardedRef } from 'react';

interface GameEndScreenMilepostProps {
	className?: string;
	progressStarsEarned?: number;
	totalStars: number;
	earnedStars: number;
	previousEarned?: number;
	levelImage?: string;
	levelName?: string;
	levelTitle?: string;
	levelCompleted?: any;
	showProgressAnimation: boolean;
	onReplay: () => void;
	onClose: () => void;
	replayButtonLabel?: string;
	closeButtonLabel?: string;
}

const achievementScaleMap = {
	[SCREEN_SIZES.MOBILE]: [7, 3, 4],
	[SCREEN_SIZES.TABLET]: [10, 5, 6],
	[SCREEN_SIZES.DESKTOP]: [15, 8, 9],
};

const getAchievementScaleMap = () => {
	const windowWidth = window.innerWidth;
	if (windowWidth < SCREEN_SIZES.MOBILE) {
		return achievementScaleMap[SCREEN_SIZES.MOBILE];
	}
	if (windowWidth < SCREEN_SIZES.TABLET) {
		return achievementScaleMap[SCREEN_SIZES.TABLET];
	}
	return achievementScaleMap[SCREEN_SIZES.DESKTOP];
};

const assignForwardedRef = <T,>(forwardedRef: ForwardedRef<T>, value: T | null) => {
	if (typeof forwardedRef === 'function') {
		forwardedRef(value);
		return;
	}
	if (forwardedRef) {
		forwardedRef.current = value;
	}
};

const GameEndScreenMilepost = forwardRef<HTMLDivElement, GameEndScreenMilepostProps>(
	(
		{
			className,
			progressStarsEarned,
			totalStars,
			earnedStars,
			previousEarned,
			levelImage,
			levelName,
			levelTitle,
			levelCompleted,
			showProgressAnimation,
			onReplay,
			onClose,
			replayButtonLabel,
			closeButtonLabel,
		},
		ref,
	) => {
		const { t } = useTranslation();
		const containerRef = useRef<HTMLDivElement>(null);
		const milepostRef = useRef<HTMLDivElement>(null);
		const achievementRef = useRef<HTMLElement>(null);

		useEffect(() => {
			if (achievementRef.current && !showProgressAnimation) {
				gsap.set(achievementRef.current, { opacity: 0 });
			}
		}, [showProgressAnimation]);

		useEffect(() => {
			if (!showProgressAnimation || !levelCompleted || !achievementRef.current) {
				return undefined;
			}

			const ctx = gsap.context(() => {
				const tl = gsap.timeline();
				const achievementScale = getAchievementScaleMap();
				const rect = achievementRef.current?.getBoundingClientRect();
				const containerRect = containerRef.current?.getBoundingClientRect();

				if (!rect || !containerRect) {
					return;
				}

				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;
				const centerX = (viewportWidth - rect.width) / 2 - containerRect.left;
				const centerY = (viewportHeight - rect.height) / 2 - containerRect.top;
				const originalTop = rect.top - containerRect.top;
				const originalLeft = rect.left - containerRect.left + rect.width / 2;

				tl.set(achievementRef.current, {
					position: 'fixed',
					scale: achievementScale[0],
					left: centerX,
					top: centerY,
					opacity: 0,
				});
				tl.to(achievementRef.current, {
					opacity: 1,
					scale: achievementScale[1],
					duration: 0.3,
					ease: Power1.easeInOut,
				});
				tl.to(achievementRef.current, {
					opacity: 1,
					scale: achievementScale[2],
					duration: 0.3,
					ease: Power1.easeInOut,
				});
				tl.to(achievementRef.current, {
					opacity: 1,
					scale: 1,
					duration: 0.3,
					delay: 0.5,
					top: originalTop,
					left: originalLeft,
					ease: Power1.easeInOut,
					onComplete: () => {
						milepostRef.current?.classList.add('active-challenge__milepost--completed');
						gsap.set(achievementRef.current, { clearProps: 'all' });
					},
				});
			}, milepostRef);

			return () => ctx.revert();
		}, [levelCompleted, showProgressAnimation]);

		return (
			<div
				className={classNames('game-end-screen-milepost', className)}
				ref={(element) => {
					containerRef.current = element;
					assignForwardedRef(ref, element);
				}}
			>
				<MilepostProgress
					ref={milepostRef}
					title={levelTitle}
					levelImage={levelImage}
					levelName={levelName}
					levelCompleted={levelCompleted}
					achievementRef={achievementRef}
					starProgress={{
						total: totalStars,
						earned: earnedStars,
						previousEarned,
						showAnimation: showProgressAnimation,
						withAnimation: true,
					}}
					className="game-end-screen-milepost__content"
				/>
				<div className="game-end-screen-milepost__stars-text">
					{progressStarsEarned && progressStarsEarned > 0
						? t('free_play.category.stars_earned', {
								count: progressStarsEarned,
						  })
						: t('free_play.category.no_stars_earned')}
				</div>
				<div className="game-end-screen-milepost__buttons">
					<Button
						type="secondary"
						tabIndex={0}
						onClick={onReplay}
						iconName="icon-repeat"
						title={replayButtonLabel || t('time_game.endscreen.replay')}
						color="white"
					/>
					<Button
						type="primary"
						tabIndex={0}
						onClick={onClose}
						iconName="icon-close"
						title={closeButtonLabel || t('time_game.endscreen.close')}
					/>
				</div>
			</div>
		);
	},
);

GameEndScreenMilepost.displayName = 'GameEndScreenMilepost';

export default GameEndScreenMilepost;
